import * as Sentry from '@sentry/react'
import './PlanADesktopApp.css'
import {
  ApiClient,
  AuthenticationProvider,
} from '@alexandrainst/plana-react-api'
import '@fontsource/roboto/300.css'
import '@fontsource/roboto/400.css'
import '@fontsource/roboto/500.css'
import '@fontsource/roboto/700.css'
import { useAppDispatch, useAppSelector } from '@/app'
import { useInstallation } from '@/features/app-information'
import { AuthenticatedPlanAProvider, LoginStateHandler } from '@/features/auth'
import {
  BackendSelector,
  selectors as coreSelectors,
  ResourceStreamSynchronizer,
} from '@/features/core'
import { MuiLocalizationProvider } from '@/features/i18n/MuiLocalizationProvider'
import { useServiceWorker } from '@/features/pwa'
import { actions as shellActions } from '@/shell'
import { useEffect } from 'react'
import '@/dayjs-extensions'
import { ApplicationQueryClientProvider } from '@/app/ApplicationQeueryClientProvider'
import { PLANA_HEADERS } from '@/app/constants'
import { CssBaseline, ThemeProvider } from '@mui/material'
import { createTheme } from '@mui/material/styles'
import { DesktopMainShellView } from './DesktopMainShellView'

const theme = createTheme({})

const saveToLocalStorage = (value: string) =>
  localStorage.setItem('plana_credentials', value)
const loadFromLocalStorage = () =>
  localStorage.getItem('plana_credentials') ?? ''
const removeFromLocalStorage = () =>
  localStorage.removeItem('plana_credentials')

const saveToSessionStorage = (value: string) =>
  window.sessionStorage.setItem('plana_credentials', value)
const loadFromSessionStorage = () =>
  window.sessionStorage.getItem('plana_credentials') ?? ''
const removeFromSessionStorage = () =>
  window.sessionStorage.removeItem('plana_credentials')

export const PlanADesktopApp = () => {
  useServiceWorker()

  const dispatch = useAppDispatch()
  const backend = useAppSelector(coreSelectors.selectBackend)
  const useZod = useAppSelector(coreSelectors.selectFlag('useZod'))
  const { installed } = useInstallation()
  const credentialsManager = {
    saveCredentials: installed ? saveToLocalStorage : saveToSessionStorage,
    loadCredentials: installed ? loadFromLocalStorage : loadFromSessionStorage,
    clearCredentials: installed
      ? removeFromLocalStorage
      : removeFromSessionStorage,
  }

  useEffect(() => {
    const onFocus = () => {
      dispatch(shellActions.appLaunched())
    }
    window.addEventListener('focus', onFocus)
    dispatch(shellActions.appLaunched())
    return () => window.removeEventListener('focus', onFocus)
  }, [dispatch])

  if (backend.valid) {
    return (
      <ThemeProvider theme={theme}>
        <div className="App">
          <MuiLocalizationProvider>
            <CssBaseline />
            <AuthenticationProvider
              offline={true}
              credentialManager={credentialsManager}
              host={backend.baseUrl}
            >
              <AuthenticatedPlanAProvider
                client={
                  new ApiClient({
                    host: backend.baseUrl,
                    headers: PLANA_HEADERS,
                    useZod: useZod,
                    onZodError: e =>
                      Sentry.captureMessage(`Zod error detected ${e}`),
                  })
                }
              >
                <ApplicationQueryClientProvider>
                  <LoginStateHandler />
                  <ResourceStreamSynchronizer />
                  <DesktopMainShellView />
                </ApplicationQueryClientProvider>
              </AuthenticatedPlanAProvider>
            </AuthenticationProvider>
          </MuiLocalizationProvider>
        </div>
      </ThemeProvider>
    )
  }

  return <BackendSelector />
}

export default PlanADesktopApp
